import React from 'react';
import TextField from '@mui/material/TextField';
import { Theme, useTheme } from '@mui/material';

interface ContactFormTextInputOptions {
  required?: boolean;
  variant?: 'standard' | 'outlined';
  multiline?: boolean;
  rows?: number;
  error?: boolean;
  helper_text?: string;
}

interface ContactFormTextInputProps {
  id: string;
  label: string;
  value: string;
  handleInputChange: (value: string) => void;
  options?: ContactFormTextInputOptions;
}

/**
 * Styled text input to use on contact us form.
 * @param {string} id ID of input on form.
 * @param {string} label Label of input to display on form.
 * @param {string} value Controlled value of input from parent.
 * @param {void} handleInputChange Update value of form field in parent.
 * @param {ContactFormTextInputOptions | undefined} options Optional additional customization to give to input.
 * @returns {JSX.Element}
 */
const ContactFormTextInput = ({
  id,
  label,
  value,
  handleInputChange,
  options
}: ContactFormTextInputProps) => {
  const theme: Theme = useTheme();
  return (
    <TextField
      id={id}
      label={label}
      onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        handleInputChange(event.target.value)
      }
      value={value}
      multiline={options?.multiline}
      rows={options?.rows}
      required={options?.required}
      variant={options?.variant}
      error={options?.error}
      helperText={options?.helper_text}
      fullWidth
      sx={{
        '& .MuiFormLabel-root': {
          color: theme.palette.secondary.dark
        },
        '& .MuiInputBase-root': {
          color: theme.palette.secondary.dark
        },
        '& .MuiInput-underline:before': {
          borderBottomColor: theme.palette.secondary.dark
        },
        '& .MuiInputBase-input': {
          color: theme.palette.primary.main
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottomColor: theme.palette.secondary.dark
        },
        '& .MuiInput-underline:after': {
          borderBottomColor: theme.palette.primary.main
        },
        '& .MuiFormLabel-root.Mui-focused': {
          color: theme.palette.primary.main
        },
        '& .MuiInputBase-root.Mui-focused': {
          color: theme.palette.primary.main
        }
      }}
    />
  );
};

export default ContactFormTextInput;
